import * as React from "react";
import { cn } from "@/lib/utils";
import { useFieldControl } from "./Field";

type Props = Omit<React.InputHTMLAttributes<HTMLInputElement>, "type" | "size"> & {
  size?: "sm" | "md";
  /** Visible label to the right of the dot. Omit it and supply `aria-label`. */
  children?: React.ReactNode;
};

/** Round radio: hairline when unchecked, indigo fill + white dot when checked.
 *  Same structure and states as Checkbox — only the shape and the glyph differ.
 *  Group them by giving every member the same `name`, inside a container with
 *  role="radiogroup" and a group label. */
export const Radio = React.forwardRef<HTMLInputElement, Props>(
  (
    { size = "md", className, children, disabled, id, "aria-describedby": describedBy, ...props },
    ref,
  ) => {
    // No special case: a radio group belongs in `<Field group>`, which hands out
    // no id at all, so members can't collide and the group label is wired by
    // aria-labelledby instead of a `for` that could only address one of them.
    const f = useFieldControl({ id, describedBy });

    return (
      <label
        className={cn(
          "vb-check",
          "vb-check--radio",
          size === "sm" && "vb-check--sm",
          disabled && "vb-check--disabled",
          className,
        )}
      >
        <input
          ref={ref}
          type="radio"
          className="vb-check__input"
          id={f.id}
          aria-describedby={f.describedBy}
          aria-invalid={f.invalid || undefined}
          disabled={disabled}
          {...props}
        />
        <span className="vb-check__box" aria-hidden="true">
          <span className="vb-check__dot" />
        </span>
        {children && <span className="vb-check__label">{children}</span>}
      </label>
    );
  },
);
Radio.displayName = "Radio";

export default Radio;
